<script setup>
import { computed, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useTickets } from "../../Composables/useTickets.js"
import { useAuthUserStore } from "../../Store/AuthUserStore.js"
import _ from 'lodash'
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"
import FormSelectField from "../../Components/FormSelectField.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()
const authUserStore = useAuthUserStore()
const authUser = computed(() => authUserStore.authUser)

const storageKey = "Shiftech_TicketList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const {
    url,
    data: tickets,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    search,
    loadData,
} = useDataManager({
    baseUrl: '/api/tickets',
    baseSearchUrl: '/api/search/tickets',
    defaultSortingField: 'updated_at',
    defaultSortingFieldsOrder: {
        id: 'asc',
        priority: 'asc',
        updated_at: 'desc',
    },
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const columns = ref([
    { label: '#ID', field: 'id', sortable: true, slot: 'id', class: 'col-1' },
    { label: 'Titre', field: 'title', class: 'col-3' },
    { label: 'Créé par', slot: 'user', class: 'col-1' },
    { label: 'Statut', slot: 'status', class: 'col-1 text-center' },
    { label: 'Assigné à', slot: 'assigned_to', class: 'col-1' },
    { label: 'Priorité', field: 'priority', sortable: true, slot: 'priority', class: 'col-1' },
    { label: 'Dernière MAJ', field: 'updated_at', sortable: true, slot: 'updated_at', class: 'col-1' },
    { label: 'Notifications', slot: 'notifications', class: 'col-1 text-center' },
    { label: 'Détails', slot: 'details', class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

const errors = ref(null)
const filters = reactive({
    user: null,
    status: null,
})
const filtersApplied = ref(true)
const isInitialized = ref(false)

onBeforeMount(() => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        url.value = savedSettings.url || url.value
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
        filtersApplied.value = savedSettings.filtersApplied ?? true

        if (savedSettings.filters) {
            Object.assign(filters, savedSettings.filters)
        }
    }

    loadData(url.value)
})

onMounted(() => {
    isInitialized.value = true
})

watch(
    [url, sortingField, sortingFieldsOrder, query, searchLoaded, filtersApplied, filters],
    ([newUrl, newSortingField, newSortingOrder, newQuery, newSearchLoaded, newFiltersApplied]) => {
        saveToLocalStorage({
            url: newUrl,
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
            query: newQuery,
            searchLoaded: newSearchLoaded,
            filtersApplied: newFiltersApplied,
            filters: { ...filters },
        })
    },
    { deep: true }
)

watch(
    () => [filters.user, filters.status],
    () => {
        if (isInitialized.value) {
            filtersApplied.value = false
        }
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

// Notifications non lues de l'utilisateur sur ce ticket : même calcul que la bulle du menu
function unreadCount(ticket) {
    const data = ticket.data
    let count = 0

    if (authUser.value.id === data.user_id) {
        count += data.notifications_user
    }
    if (authUser.value.id === data.assigned_to_id) {
        count += data.notifications_assigned
    }
    // File des tickets pas encore pris en charge
    if (data.assigned_to_id === null && data.user_id !== authUser.value.id && ['admin', 'back_office'].includes(authUser.value.role)) {
        count += data.notifications_bo
    }

    return count
}

function applyFilter() {
    let newUrl = new URL(url.value, window.location.origin)

    Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== '') {
            newUrl.searchParams.set(key, value)
        } else {
            newUrl.searchParams.delete(key)
        }
    })

    filtersApplied.value = true
    loadData(newUrl.href)
}

const idToDelete = ref(null)

function showModal(modalId, id) {
    idToDelete.value = id
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

const {
    deleteTicket,
} = useTickets({
    onDeleted: () => {
        loadData(url.value)
        hideModal('modalDelete')
    },
    onError: (error) => httpErrorsHandler(error),
})
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-header border-0">
                    <div class="d-flex align-items-center">
                        <h5 class="card-title mb-0 flex-grow-1">Tickets</h5>
                        <div class="flex-shrink-0">
                            <div class="d-flex flex-wrap gap-2">
                                <router-link :to="{ name: 'tickets.create' }" class="btn btn-secondary add-btn">
                                    <i class="ri-add-line align-bottom me-1"></i> Créer un ticket
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Search and Filters -->
                <div class="card-body border border-dashed border-end-0 border-start-0">
                    <div class="row g-3">
                        <div class="col-xxl-5 col-sm-12">
                            <div class="search-box">
                                <input type="search" class="form-control search" placeholder="Rechercher..." v-model="query" @input="debouncedSearch" id="searchTickets">
                                <i class="ri-search-line search-icon"></i>
                            </div>
                        </div>
                        <form-select-field
                            name="filters.status"
                            label="Statut"
                            :label-hidden="true"
                            @update:field="filters.status = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.status"
                            bs-class="col-xxl-3 col-sm-4"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Tous les statuts"
                            :liste-options="[
                                { text: 'Statuts ouverts (A traiter, En cours)', value: 'all_open' },
                                { text: 'Statuts fermés (Résolu, Fermé)', value: 'all_closed' },
                                { text: 'Tous les statuts', value: null },
                                { text: '---', value: null },
                                { text: 'A traiter', value: 'pending' },
                                { text: 'En cours', value: 'in_progress' },
                                { text: 'Résolu', value: 'resolved' },
                                { text: 'Fermé', value: 'closed' },
                            ]"
                            margin="mb-0"
                        />
                        <form-select-field
                            name="filters.user"
                            label="Utilisateur"
                            :label-hidden="true"
                            @update:field="filters.user = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.user"
                            bs-class="col-xxl-3 col-sm-4"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Tous les tickets"
                            :liste-options="[
                                { text: 'Mes tickets', value: 'my_tickets' },
                                { text: 'Tickets qui me sont assignés', value: 'assigned_to_me' },
                                // Les tickets non assignés ne sont visibles que par l'équipe Shiftech
                                ...(['admin', 'back_office'].includes(authUser.role) ? [{ text: 'Tickets non assignés', value: 'unassigned' }] : []),
                                { text: 'Tous les tickets', value: null },
                            ]"
                            margin="mb-0"
                        />
                        <div class="col-xxl-1 col-sm-4">
                            <button type="button" class="btn w-100" @click.prevent="applyFilter" :class="filtersApplied ? 'btn-dark' : 'btn-warning'">
                                <i class="ri-equalizer-fill me-1 align-bottom"></i>
                                Filtrer
                            </button>
                        </div>
                    </div>
                </div>

                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="tickets"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- ID -->
                        <template #id="{ item }">
                            <span class="fw-medium text-dark">#{{ item.data.id }}</span>
                        </template>

                        <!-- User -->
                        <template #user="{ item }">
                            <div class="d-flex align-items-center">
                                <div class="flex-shrink-0">
                                    <img :src="'/format-image/'+item.data.user.data.profile_picture+'?w=32&h=32&fm=webp&fit=crop'" alt="" class="avatar-xs rounded-circle">
                                </div>
                                <div class="flex-grow-1 ms-2 name">{{ item.data.user.data.last_name }} {{ item.data.user.data.first_name }}</div>
                            </div>
                        </template>

                        <!-- Status -->
                        <template #status="{ item }">
                            <span :class="'badge fs-11 bg-'+item.data.status_badge_color+'-subtle text-'+item.data.status_badge_color">
                                {{ item.data.status_label_fr }}
                            </span>
                        </template>

                        <!-- Assigned to -->
                        <template #assigned_to="{ item }">
                            <div class="d-flex align-items-center" v-if="item.data.assigned_to !== null">
                                <div class="flex-shrink-0">
                                    <img :src="'/format-image/'+item.data.assigned_to.data.profile_picture+'?w=32&h=32&fm=webp&fit=crop'" alt="" class="avatar-xs rounded-circle">
                                </div>
                                <div class="flex-grow-1 ms-2 name">{{ item.data.assigned_to.data.last_name }} {{ item.data.assigned_to.data.first_name }}</div>
                            </div>
                            <span v-else>--</span>
                        </template>

                        <!-- Priority -->
                        <template #priority="{ item }">
                            <span :class="'badge bg-'+item.data.priority_badge_color+'-subtle text-'+item.data.priority_badge_color+' badge-border fs-11'">
                                {{ item.data.priority_label_fr }}
                            </span>
                        </template>

                        <!-- Updated at -->
                        <template #updated_at="{ item }">
                            {{ item.data.updated_at_fr }}
                        </template>

                        <!-- Notifications -->
                        <template #notifications="{ item }">
                            <!-- Seulement ses propres notifications : celles qu'ouvrir le ticket efface -->
                            <span v-if="unreadCount(item) > 0" class="badge rounded-pill bg-danger fs-11">{{ unreadCount(item) }}</span>
                        </template>

                        <!-- Details -->
                        <template #details="{ item }">
                            <router-link :to="{ name: 'tickets.show', params: { id: item.data.id } }" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm">
                                Accéder
                            </router-link>
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- UPDATE -->
                            <router-link :to="{ name: 'tickets.edit', params: { id: item.data.id } }" class="text-info" title="Modifier le ticket" v-if="item.can.update">
                                <i class="ri-pencil-fill fs-17 px-1"></i>
                            </router-link>
                            <i class="ri-pencil-fill fs-17 px-1 text-muted" v-else></i>

                            <!-- DELETE -->
                            <a href="#" class="text-danger" title="Supprimer le ticket" @click.prevent="showModal('modalDelete', item.data.id)" v-if="item.can.delete">
                                <i class="ri-delete-bin-3-fill fs-17 px-1"></i>
                            </a>
                            <i class="ri-delete-bin-3-fill fs-17 px-1 text-muted" v-else></i>
                        </template>
                    </data-table>

                    <modal id="modalDelete" title="Supprimer le ticket ?" btn-class="btn-danger" btn-text="Supprimer" @click:button="deleteTicket(idToDelete)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point de supprimer un ticket. Continuer ?</p>
                        </template>
                    </modal>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
